import { useState, useEffect, useRef } from 'react';
import { useWallet } from '@aptos-labs/wallet-adapter-react';
import './App.css';

interface Dataset {
  name: string;
  format: string;
  size: number;
  status: string;
  expiresAt: string;
  createdAt: string;
  owner: string;
}

const API_KEY = process.env.REACT_APP_SHELBY_API_KEY || '';
const SHELBY_UPLOAD_URL =
  process.env.REACT_APP_SHELBY_UPLOAD_URL || 'https://testnet.shelby.xyz/v1/blobs';
const INDEXER_URL =
  'https://api.testnet.aptoslabs.com/nocode/v1/public/cmlfqs5wt00qrs601zt5s4kfj/v1/graphql';

const FORMAT_DESCRIPTIONS: Record<string, string> = {
  VCF: 'Variant Call Format — genomic variants and SNPs',
  FASTQ: 'Raw sequencing reads from next-gen sequencing',
  SAM: 'Sequence Alignment Map — read alignments',
  BAM: 'Binary Alignment Map — compressed alignments',
  FASTA: 'Reference genome sequences',
};

const FORMAT_DEFAULT_PRICES: Record<string, string> = {
  VCF: '0.01',
  FASTQ: '0.02',
  SAM: '0.02',
  BAM: '0.02',
  FASTA: '0.01',
};

// Fallback install URLs for wallets not in the adapter's standard registry
const WALLET_INSTALL_URLS: Record<string, string> = {
  Martian:
    'https://chromewebstore.google.com/detail/martian-aptos-wallet/efbglgofoippbgcjepnhiblaibcnclgk',
  'Pontem Wallet':
    'https://chromewebstore.google.com/detail/pontem-crypto-wallet/phkbamefinggmakgklpkljjmgibohnba',
};

function getFormat(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith('.vcf')) return 'VCF';
  if (lower.endsWith('.fastq') || lower.endsWith('.fq')) return 'FASTQ';
  if (lower.endsWith('.sam')) return 'SAM';
  if (lower.endsWith('.bam')) return 'BAM';
  if (lower.endsWith('.fasta') || lower.endsWith('.fa')) return 'FASTA';
  return 'Unknown';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function shortAddr(addr: string): string {
  if (!addr) return 'Unknown';
  if (addr.length <= 13) return addr;
  return addr.slice(0, 6) + '…' + addr.slice(-4);
}

function priceKey(owner: string, name: string): string {
  return `ps_price_${owner}_${name}`;
}

function getStoredPrice(owner: string, name: string): string | null {
  try {
    return localStorage.getItem(priceKey(owner, name));
  } catch {
    return null;
  }
}

function saveStoredPrice(owner: string, name: string, price: string): void {
  try {
    localStorage.setItem(priceKey(owner, name), price);
  } catch {}
}

function App() {
  const {
    account,
    connected,
    wallets,
    notDetectedWallets,
    connect,
    disconnect,
  } = useWallet();

  const walletAddress = account?.address ? String(account.address) : '';

  // Dataset state
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Access modal state
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [grantRequested, setGrantRequested] = useState(false);

  // Wallet menu state
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [walletError, setWalletError] = useState('');
  const walletMenuRef = useRef<HTMLDivElement>(null);

  // Upload modal state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPrice, setUploadPrice] = useState('0.01');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inline price editing state
  const [editingPriceKey, setEditingPriceKey] = useState<string | null>(null);
  const [editPriceVal, setEditPriceVal] = useState('');
  // Bump to force re-render after price save
  const [priceRevision, setPriceRevision] = useState(0);

  useEffect(() => {
    fetchDatasets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close wallet menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (walletMenuRef.current && !walletMenuRef.current.contains(e.target as Node)) {
        setShowWalletMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-set default price when selected file format changes
  useEffect(() => {
    if (uploadFile) {
      const fmt = getFormat(uploadFile.name);
      setUploadPrice(FORMAT_DEFAULT_PRICES[fmt] || '0.01');
    }
  }, [uploadFile]);

  const fetchDatasets = async () => {
    try {
      setLoading(true);
      setError('');
      const query = `
        query {
          blobs(limit: 500) {
            blob_name
            size
            expires_at
            created_at
            is_written
            is_deleted
          }
        }
      `;

      const res = await fetch(INDEXER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${API_KEY}`,
        },
        body: JSON.stringify({ query }),
      });

      const json = await res.json();
      console.log('Indexer response:', JSON.stringify(json, null, 2));

      const blobs = (json?.data?.blobs || []).filter(
        (b: any) => b.is_deleted === '0'
      );

      const mapped: Dataset[] = blobs.map((b: any) => {
        const hasSlash = b.blob_name.includes('/');
        const parts = hasSlash ? b.blob_name.split('/') : ['', b.blob_name];
        return {
          name: parts.slice(1).join('/') || b.blob_name,
          format: getFormat(b.blob_name),
          size: parseInt(b.size),
          status: b.is_written === '1' ? 'stored' : 'pending',
          expiresAt: new Date(parseInt(b.expires_at) / 1000).toLocaleDateString(),
          createdAt: new Date(parseInt(b.created_at) / 1000).toLocaleDateString(),
          owner: parts[0],
        };
      });

      setDatasets(mapped);
    } catch {
      setError('Failed to load datasets. Check your API key and network.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (walletName: string) => {
    setWalletError('');
    try {
      connect(walletName);
      setShowWalletMenu(false);
    } catch {
      setWalletError(`Could not connect to ${walletName}. Make sure it is installed.`);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !walletAddress) return;
    setUploadLoading(true);
    setUploadError('');
    try {
      const url = `${SHELBY_UPLOAD_URL}/${walletAddress}/${uploadFile.name}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${API_KEY}`,
          'Content-Type': 'application/octet-stream',
        },
        body: uploadFile,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      saveStoredPrice(walletAddress, uploadFile.name, uploadPrice);
      setUploadSuccess(true);
      setTimeout(() => {
        setShowUpload(false);
        setUploadSuccess(false);
        setUploadFile(null);
        setUploadPrice('0.01');
        fetchDatasets();
      }, 2500);
    } catch (e: any) {
      setUploadError(e.message || 'Upload failed. Verify the Shelby upload endpoint.');
    } finally {
      setUploadLoading(false);
    }
  };

  const getDisplayPrice = (owner: string, name: string, format: string): string => {
    // priceRevision is read here so saving a price triggers a re-render
    void priceRevision;
    const stored = getStoredPrice(owner, name);
    return `${stored || FORMAT_DEFAULT_PRICES[format] || '0.01'} ShelbyUSD / read`;
  };

  const dKey = (d: Dataset) => `${d.owner}/${d.name}`;

  const handleEditPrice = (d: Dataset) => {
    setEditingPriceKey(dKey(d));
    setEditPriceVal(
      getStoredPrice(d.owner, d.name) || FORMAT_DEFAULT_PRICES[d.format] || '0.01'
    );
  };

  const handleSavePrice = (d: Dataset) => {
    saveStoredPrice(d.owner, d.name, editPriceVal);
    setEditingPriceKey(null);
    setPriceRevision((v) => v + 1);
  };

  // Build the three target wallet options (Petra, Martian, Pontem)
  const targetWallets = ['Petra', 'Martian', 'Pontem Wallet'].map((name) => {
    const installed = (wallets as any[]).find((w: any) => w.name === name);
    const undetected = (notDetectedWallets as any[]).find((w: any) => w.name === name);
    const meta = installed || undetected;
    return {
      name,
      icon: meta?.icon as string | undefined,
      url: (meta?.url as string) || WALLET_INSTALL_URLS[name] || '',
      isInstalled: !!installed,
    };
  });

  const myDatasetsCount = walletAddress
    ? datasets.filter((d) => d.owner.toLowerCase() === walletAddress.toLowerCase()).length
    : 0;

  return (
    <div className="app">
      <header className="header">
        <div className="header-inner">
          <div className="logo">
            <span className="logo-icon">🧬</span>
            <span className="logo-text">PharmaServes</span>
          </div>
          <div className="header-right">
            {connected && (
              <button
                className="btn-upload"
                onClick={() => {
                  setUploadFile(null);
                  setUploadError('');
                  setUploadSuccess(false);
                  setShowUpload(true);
                }}
              >
                ↑ Upload Dataset
              </button>
            )}

            <div className="wallet-area" ref={walletMenuRef}>
              {connected ? (
                <div className="wallet-connected-row">
                  <span className="wallet-dot" />
                  <span className="wallet-addr" title={walletAddress}>
                    {shortAddr(walletAddress)}
                  </span>
                  <button className="btn-disconnect" onClick={disconnect}>
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="wallet-dropdown">
                  <button
                    className="btn-connect-wallet"
                    onClick={() => {
                      setWalletError('');
                      setShowWalletMenu((v) => !v);
                    }}
                  >
                    Connect Wallet
                  </button>
                  {showWalletMenu && (
                    <div className="wallet-menu">
                      <div className="wallet-menu-title">Select Wallet</div>
                      {targetWallets.map(({ name, icon, url, isInstalled }) => (
                        <button
                          key={name}
                          className="wallet-option"
                          onClick={() => handleConnect(name)}
                        >
                          {icon ? (
                            <img src={icon} alt="" className="wallet-icon-img" />
                          ) : (
                            <span className="wallet-icon-placeholder" />
                          )}
                          <span className="wallet-option-name">{name}</span>
                          {isInstalled ? (
                            <span className="wallet-state installed">Installed</span>
                          ) : (
                            <a
                              className="wallet-state not-installed"
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Install ↗
                            </a>
                          )}
                        </button>
                      ))}
                      {walletError && (
                        <div className="wallet-error">{walletError}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="hero">
          <h1>
            African Genomic Data,
            <br />
            For the World
          </h1>
          <p>
            Stream genomic datasets directly into your drug discovery pipeline. Pay per
            read. African biobanks earn in real time.
          </p>
          <div className="stats">
            <div className="stat">
              <span className="stat-number">{datasets.length}</span>
              <span className="stat-label">Datasets</span>
            </div>
            <div className="stat">
              <span className="stat-number">
                {datasets.filter((d) => d.status === 'stored').length}
              </span>
              <span className="stat-label">Available</span>
            </div>
            <div className="stat">
              <span className="stat-number">537ms</span>
              <span className="stat-label">Avg Read</span>
            </div>
            <div className="stat">
              <span className="stat-number">16</span>
              <span className="stat-label">Storage Nodes</span>
            </div>
            {connected && myDatasetsCount > 0 && (
              <div className="stat stat-mine">
                <span className="stat-number">{myDatasetsCount}</span>
                <span className="stat-label">Your Datasets</span>
              </div>
            )}
          </div>
        </div>

        <div className="section">
          <div className="section-title-row">
            <h2>Available Datasets</h2>
            {connected && (
              <div className="own-legend">
                <span className="own-legend-dot" />
                <span>Your datasets</span>
              </div>
            )}
          </div>
          {loading && (
            <div className="loading">Loading datasets from Shelby testnet…</div>
          )}
          {error && <div className="error">{error}</div>}
          {!loading && !error && datasets.length === 0 && (
            <div className="loading">No datasets found on the marketplace.</div>
          )}
          {!loading && !error && datasets.length > 0 && (
            <div className="dataset-grid">
              {datasets.map((d, i) => {
                const isOwn =
                  !!walletAddress &&
                  d.owner.toLowerCase() === walletAddress.toLowerCase();
                const key = dKey(d);
                const isEditing = editingPriceKey === key;

                return (
                  <div
                    key={i}
                    className={`dataset-card ${d.status === 'stored' ? 'available' : 'pending'}${isOwn ? ' own-dataset' : ''}`}
                  >
                    <div className="card-header">
                      <span className={`format-badge ${d.format.toLowerCase()}`}>
                        {d.format}
                      </span>
                      <div className="card-badges">
                        {isOwn && <span className="own-badge">Yours</span>}
                        <span className={`status-badge ${d.status}`}>
                          {d.status === 'stored' ? '✓ Available' : '⏳ Pending'}
                        </span>
                      </div>
                    </div>

                    <div className="card-name">{d.name.split('/').pop()}</div>
                    <div className="card-path">{d.name}</div>
                    <div className="card-owner">
                      Owner:{' '}
                      <span title={d.owner}>{isOwn ? 'You' : shortAddr(d.owner)}</span>
                    </div>
                    <div className="card-desc">
                      {FORMAT_DESCRIPTIONS[d.format] || 'Genomic dataset'}
                    </div>
                    <div className="card-meta">
                      <span>{formatSize(d.size)}</span>
                      <span>Added {d.createdAt}</span>
                      <span>Expires {d.expiresAt}</span>
                    </div>

                    {isOwn ? (
                      <div className="card-price-row">
                        {isEditing ? (
                          <div className="price-edit-inline">
                            <input
                              className="price-input-sm"
                              type="number"
                              step="0.001"
                              min="0"
                              value={editPriceVal}
                              onChange={(e) => setEditPriceVal(e.target.value)}
                            />
                            <span className="price-unit-sm">ShelbyUSD / read</span>
                            <button
                              className="btn-save-price"
                              onClick={() => handleSavePrice(d)}
                            >
                              Save
                            </button>
                            <button
                              className="btn-cancel-price"
                              onClick={() => setEditingPriceKey(null)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="card-price">
                              {getDisplayPrice(d.owner, d.name, d.format)}
                            </span>
                            <button
                              className="btn-edit-price"
                              title="Edit price"
                              onClick={() => handleEditPrice(d)}
                            >
                              ✏
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="card-price">
                        {getDisplayPrice(d.owner, d.name, d.format)}
                      </div>
                    )}

                    {d.status === 'stored' && !isOwn && (
                      <button
                        className="btn-access"
                        onClick={() => {
                          setSelectedDataset(d);
                          setGrantRequested(false);
                        }}
                      >
                        Request Access
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="section">
          <h2>Why PharmaServes?</h2>
          <div className="features">
            <div className="feature">
              <div className="feature-icon">⚡</div>
              <h3>Sub-second Reads</h3>
              <p>
                537ms average read latency from decentralized storage. Stream genomic
                regions directly into AlphaFold or AutoDock Vina.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">🔐</div>
              <h3>Encrypted Access</h3>
              <p>
                Time-limited encrypted grants. Biobanks control exactly who accesses
                their data and for how long.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">💰</div>
              <h3>Fair Compensation</h3>
              <p>
                African biobanks earn ShelbyUSD micropayments in real time, every read.
                No middlemen.
              </p>
            </div>
            <div className="feature">
              <div className="feature-icon">🌍</div>
              <h3>African First</h3>
              <p>
                Built specifically for African genomic diversity — the most
                underrepresented data in global drug discovery.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Access request modal */}
      {selectedDataset && (
        <div className="modal-overlay" onClick={() => setSelectedDataset(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedDataset(null)}>
              ✕
            </button>
            <h2>Request Access</h2>
            <div className="modal-dataset">
              <span className={`format-badge ${selectedDataset.format.toLowerCase()}`}>
                {selectedDataset.format}
              </span>
              <span className="modal-name">{selectedDataset.name.split('/').pop()}</span>
            </div>
            <div className="modal-detail">
              <span>Owner</span>
              <span title={selectedDataset.owner}>{shortAddr(selectedDataset.owner)}</span>
              <span>Size</span>
              <span>{formatSize(selectedDataset.size)}</span>
              <span>Price</span>
              <span>
                {getDisplayPrice(
                  selectedDataset.owner,
                  selectedDataset.name,
                  selectedDataset.format
                )}
              </span>
              <span>Access Duration</span>
              <span>24 hours</span>
              <span>Expires</span>
              <span>{selectedDataset.expiresAt}</span>
            </div>
            {!grantRequested ? (
              <>
                <p className="modal-note">
                  You will receive an encrypted 24-hour access grant. Decrypt it using
                  pharmaserves-sdk to stream this dataset directly into your research
                  pipeline.
                </p>
                <button className="btn-confirm" onClick={() => setGrantRequested(true)}>
                  Confirm Access Request
                </button>
              </>
            ) : (
              <div className="grant-success">
                <div className="grant-icon">✓</div>
                <h3>Access Grant Issued</h3>
                <p>
                  Your encrypted 24-hour grant has been generated. Use pharmaserves-sdk
                  to decrypt and stream this dataset.
                </p>
                <code>npm install pharmaserves-sdk</code>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div
          className="modal-overlay"
          onClick={() => !uploadLoading && setShowUpload(false)}
        >
          <div className="modal modal-upload" onClick={(e) => e.stopPropagation()}>
            {!uploadLoading && (
              <button className="modal-close" onClick={() => setShowUpload(false)}>
                ✕
              </button>
            )}
            {uploadSuccess ? (
              <div className="grant-success">
                <div className="grant-icon">✓</div>
                <h3>Dataset Uploaded</h3>
                <p>
                  Your dataset is being indexed on Shelby testnet and will appear in the
                  marketplace shortly.
                </p>
              </div>
            ) : (
              <>
                <h2>Upload Dataset</h2>
                <p className="modal-note">
                  Upload a genomic file to Shelby under your wallet address. You set the
                  price per read.
                </p>

                <div className="upload-field">
                  <label className="upload-label">File</label>
                  <div
                    className={`upload-drop${uploadFile ? ' has-file' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadFile ? (
                      <span className="upload-filename">
                        {uploadFile.name} ({formatSize(uploadFile.size)})
                      </span>
                    ) : (
                      <span className="upload-placeholder">
                        Click to select a VCF, FASTQ, SAM, BAM, or FASTA file
                      </span>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".vcf,.fastq,.fq,.sam,.bam,.fasta,.fa"
                    style={{ display: 'none' }}
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                </div>

                <div className="upload-field">
                  <label className="upload-label">Price per Read (ShelbyUSD)</label>
                  <div className="price-input-row">
                    <input
                      className="price-input-main"
                      type="number"
                      step="0.001"
                      min="0"
                      value={uploadPrice}
                      onChange={(e) => setUploadPrice(e.target.value)}
                    />
                    <span className="price-unit-main">ShelbyUSD / read</span>
                  </div>
                </div>

                <div className="upload-field">
                  <label className="upload-label">Owner (your wallet)</label>
                  <div className="upload-owner">{walletAddress}</div>
                </div>

                {uploadError && (
                  <div className="error upload-error-msg">{uploadError}</div>
                )}

                <button
                  className="btn-confirm"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploadLoading}
                >
                  {uploadLoading ? 'Uploading…' : 'Upload to Shelby'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="footer-inner">
          <span>PharmaServes — Built on Shelby Protocol</span>
          <div className="footer-links">
            <a
              href="https://github.com/bennytimz/pharmaserves"
              target="_blank"
              rel="noreferrer"
            >
              GitHub
            </a>
            <a
              href="https://www.npmjs.com/package/pharmaserves-sdk"
              target="_blank"
              rel="noreferrer"
            >
              npm
            </a>
            <a href="https://shelby.xyz" target="_blank" rel="noreferrer">
              Shelby
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
